import {
  PreloadedState,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import {
  persistReducer,
  persistStore,
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

import authReducer from '@features/auth/slices/auth-slice';
import userRegistrationReducer from '@features/auth/slices/user-registration.slice';
import permissionsReducer from '@shared/slices/permissions.slice';
import editProfileReducer from '@features/profile/slices/edit-profile.slice';
import usersReducer from '@features/users/slices/users.slice';
import userCardReducer from '@features/profile/slices/user-card.slice';
import editAdminProfileReducer from '@features/settings/slices/edit-admin-profile.slice';
import ipReducer from '@features/profile/slices/ip.slice';
import documentationReviewReducer from '@features/events/slices/documentation-review.slice';

const permissionsPersistConfig = {
  key: 'permissions',
  storage: storage,
};

const authPersistConfig = {
  key: 'auth',
  storage: storage,
  whitelist: ['userToken', 'userInfo'],
};

const persistedPermissionReducer = persistReducer(
  permissionsPersistConfig,
  permissionsReducer,
);

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);

const rootReducer = combineReducers({
  auth: persistedAuthReducer,
  userRegistration: userRegistrationReducer,
  editProfile: editProfileReducer,
  editAdminProfile: editAdminProfileReducer,
  permissions: persistedPermissionReducer,
  users: usersReducer,
  userCard: userCardReducer,
  userIp: ipReducer,
  documentationReview: documentationReviewReducer,
});

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });
};

const store = setupStore();

export const persistor = persistStore(store);

export default store;

export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = typeof store.dispatch;
